import moment from 'moment';

import { formatEntityId } from '../../../helpers';
import { TARGET_STAGE_LOG_PHASE_CHANGE_TYPE } from '../../../pages/constants';
import {
  DurationEventMeasurementUnitEnum,
  RateMethodMeasurementUnitsIntervalUnitEnum,
  TPGoalDataCollectionMethod,
} from '../treatment-plan/goals/common/constants/general';

import type {
  IDataDashboardBaseline,
  IDataDashboardDetailRow,
  IDataDashboardGoalSummary,
  IDataDashboardPhaseRow,
  IDataDashboardReport,
  IDataDashboardReportInput,
  IDataDashboardReportSession,
  IDataDashboardReportTargetData,
  IDataDashboardSessionSource,
  IDataDashboardTargetSummary,
  IDataDashboardTreatmentPlanRow,
} from './types';

function getDurationDivisor(unit?: string | null) {
  if (unit === DurationEventMeasurementUnitEnum.hour) return 3600;
  if (unit === DurationEventMeasurementUnitEnum.minute) return 60;
  return 1;
}

function getRateDuration(seconds: number, unit?: string | null, intervalSize?: number | null) {
  let duration = seconds;
  if (unit === RateMethodMeasurementUnitsIntervalUnitEnum.HOUR) duration = seconds / 3600;
  if (unit === RateMethodMeasurementUnitsIntervalUnitEnum.MINUTE) duration = seconds / 60;
  return duration / (intervalSize ?? 1);
}

function getLocalMoment(date: string, utcOffset?: number) {
  return moment.utc(date).add(utcOffset ?? 0, 'seconds');
}

function isInRange(date: string, utcOffset: number | undefined, startDate: string, endDate: string) {
  const localDate = getLocalMoment(date, utcOffset).format('YYYY-MM-DD');
  return localDate >= startDate && localDate <= endDate;
}

function buildSession(sources: IDataDashboardSessionSource[], target: string) {
  const first = sources[0];
  const attempts = sources.reduce((sum, source) => sum + source.trial.attempts, 0);
  const success = sources.reduce((sum, source) => sum + source.trial.success, 0);
  const instances = sources.reduce((sum, source) => sum + source.trial.instances, 0);
  const durations = sources.flatMap((source) => source.trial.durations ?? []);
  const totalDuration = sources.reduce(
    (sum, source) => sum + (source.trial.total_duration ?? 0),
    0,
  );
  const durationEvents = sources.flatMap((source) => source.trial.duration_events ?? []);
  const durationDivisor = getDurationDivisor(first.trial.measurement_units_duration_unit);
  const durationEventTotal = Number(
    (durationEvents.reduce((sum, duration) => sum + duration, 0) / durationDivisor).toFixed(2),
  );
  const durationEventAverage = durationEvents.length
    ? Number((durationEventTotal / durationEvents.length).toFixed(2))
    : 0;
  const rateEvents = sources.reduce(
    (sum, source) => sum + (source.trial.total_trial_rate_events ?? 0),
    0,
  );
  const rateDuration = getRateDuration(
    sources.reduce((sum, source) => sum + (source.trial.expected_duration ?? 0), 0),
    first.trial.measurement_units_interval_unit,
    first.trial.measurement_units_interval_size,
  );
  const localDate = getLocalMoment(first.trial.date, first.trial.utc_offset);

  const session: IDataDashboardReportSession = {
    ...first.trial,
    displayDate: localDate.format('MM/DD/YY'),
    timestamp: first.trial.date,
    target,
    source_target_ids: [...new Set(sources.map((source) => source.sourceTarget.target_id))],
    source_goal_ids: [...new Set(sources.map((source) => source.summary.goal_id))],
    treatment_plan_id: first.summary.treatment_plan_id,
    treatmentPlan: formatEntityId(first.summary.treatment_plan_id, 'FT'),
    attempts,
    success,
    instances,
    durations,
    total_duration: totalDuration,
    percentage: attempts ? Number(((success / attempts) * 100).toFixed(2)) : 0,
    rate: rateDuration ? Number((rateEvents / rateDuration).toFixed(2)) : 0,
    durationEventAverage,
    durationEventTotal,
  };
  return session;
}

function buildTargetData(
  selectedTarget: IDataDashboardReportInput['selectedTargets'][number],
  goalSummaries: IDataDashboardGoalSummary[],
  startDate: string,
  endDate: string,
) {
  const sourcesBySession = new Map<string, IDataDashboardSessionSource[]>();

  goalSummaries.forEach((summary) => {
    summary.targets
      .filter((sourceTarget) => selectedTarget.target_ids.includes(sourceTarget.target_id))
      .forEach((sourceTarget) => {
        sourceTarget.trials
          .filter((trial) => isInRange(trial.date, trial.utc_offset, startDate, endDate))
          .forEach((trial) => {
            const sources = sourcesBySession.get(trial.session_key) ?? [];
            sources.push({ summary, sourceTarget, trial });
            sourcesBySession.set(trial.session_key, sources);
          });
      });
  });

  const targetData: IDataDashboardReportTargetData = {
    id: selectedTarget.id,
    label: selectedTarget.label,
    sourceTargetIds: selectedTarget.target_ids,
    sessions: [...sourcesBySession.values()]
      .map((sources) => buildSession(sources, selectedTarget.label))
      .sort(
        (a, b) =>
          a.timestamp.localeCompare(b.timestamp) || a.session_key.localeCompare(b.session_key),
      ),
  };
  return targetData;
}

function buildDetailRows(targets: IDataDashboardReportTargetData[]) {
  return targets
    .flatMap((target) =>
      target.sessions.map((session): IDataDashboardDetailRow => ({
        sessionKey: session.session_key,
        timestamp: session.timestamp,
        date: session.displayDate,
        time: getLocalMoment(session.timestamp, session.utc_offset).format('h:mm A'),
        target: target.label,
        treatmentPlan: session.treatmentPlan,
        dataCollectionMethod: session.data_collection_method,
        attempts: session.attempts,
        success: session.success,
        instances: session.instances,
        percentage: session.percentage,
        rate: session.rate,
        durationAverage: session.durationEventAverage,
        durationTotal: session.durationEventTotal,
        threshold: session.threshold,
        required_count: session.required_count,
        direction: session.direction,
        threshold_status: session.threshold_status,
        in_completed_run: session.in_completed_run,
      })),
    )
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.sessionKey.localeCompare(b.sessionKey));
}

function buildPhaseRows(
  input: IDataDashboardReportInput,
  contributingGoalIds: Set<number>,
) {
  const seenPhaseChanges = new Set<string>();
  const rows: IDataDashboardPhaseRow[] = [];

  input.selectedTargets.forEach((selectedTarget) => {
    input.goalSummaries
      .filter((summary) => contributingGoalIds.has(summary.goal_id))
      .flatMap((summary) => summary.targets)
      .filter((sourceTarget) => selectedTarget.target_ids.includes(sourceTarget.target_id))
      .forEach((sourceTarget) => {
        const phaseChanges = [
          ...(sourceTarget.phase_changes ?? []).map((phaseChange) => ({
            id: `phase:${phaseChange.id}`,
            date: phaseChange.date_added,
            title: phaseChange.title,
            type: phaseChange.type,
          })),
          ...(sourceTarget.stage_change_logs ?? []).map((stageChange) => ({
            id: `stage:${stageChange.id}`,
            date: stageChange.date_stage_changed,
            title: `${stageChange.old_stage ? `${stageChange.old_stage} to ` : ''}${
              stageChange.new_stage
            }${stageChange.reason_for_change ? ` (${stageChange.reason_for_change})` : ''}`,
            type: TARGET_STAGE_LOG_PHASE_CHANGE_TYPE,
          })),
        ];
        phaseChanges.forEach((phaseChange) => {
          const key = `${sourceTarget.target_id}:${phaseChange.id}`;
          if (
            seenPhaseChanges.has(key) ||
            !moment.utc(phaseChange.date).isBetween(
              moment.utc(input.startDate).startOf('day'),
              moment.utc(input.endDate).endOf('day'),
              undefined,
              '[]',
            )
          ) {
            return;
          }
          seenPhaseChanges.add(key);
          rows.push({
            sourceTargetId: sourceTarget.target_id,
            date: moment.utc(phaseChange.date).format('MM/DD/YY'),
            timestamp: phaseChange.date,
            target: selectedTarget.label,
            title: phaseChange.title,
            type: phaseChange.type,
          });
        });
      });
  });

  return rows.sort(
    (a, b) => a.timestamp.localeCompare(b.timestamp) || a.target.localeCompare(b.target),
  );
}

function buildTreatmentPlanRows(
  goalSummaries: IDataDashboardGoalSummary[],
  contributingGoalIds: Set<number>,
) {
  const rowsByTreatmentPlan = new Map<number, IDataDashboardTreatmentPlanRow>();
  goalSummaries
    .filter((summary) => contributingGoalIds.has(summary.goal_id))
    .forEach((summary) => {
      rowsByTreatmentPlan.set(summary.treatment_plan_id, {
        treatmentPlanId: summary.treatment_plan_id,
        treatmentPlan: formatEntityId(summary.treatment_plan_id, 'FT'),
        start: summary.treatment_plan_start
          ? moment(summary.treatment_plan_start).format('MM/DD/YY')
          : '',
        end: summary.treatment_plan_end
          ? moment(summary.treatment_plan_end).format('MM/DD/YY')
          : '—',
        status: summary.treatment_plan_status,
      });
    });
  return [...rowsByTreatmentPlan.values()].sort((a, b) => a.treatmentPlanId - b.treatmentPlanId);
}

function buildBaselines(
  goalSummaries: IDataDashboardGoalSummary[],
  contributingGoalIds: Set<number>,
) {
  return goalSummaries
    .filter((summary) => contributingGoalIds.has(summary.goal_id))
    .map(
      (summary): IDataDashboardBaseline => ({
        goalId: summary.goal_id,
        treatmentPlanId: summary.treatment_plan_id,
        treatmentPlan: formatEntityId(summary.treatment_plan_id, 'FT'),
        date: summary.date_introduced,
        value: summary.baseline_at_creation,
      }),
    );
}

function buildTargetSummary(target: IDataDashboardReportTargetData) {
  const dataCollectionMethod = target.sessions[0]?.data_collection_method;
  const attempts = target.sessions.reduce((sum, session) => sum + session.attempts, 0);
  const success = target.sessions.reduce((sum, session) => sum + session.success, 0);
  const instanceDurations = target.sessions.flatMap((session) => session.durations ?? []);
  let value = 0;
  let total = 0;

  if (dataCollectionMethod === TPGoalDataCollectionMethod.PERCENTAGE) {
    value = attempts ? Number(((success / attempts) * 100).toFixed(1)) : 0;
  } else if (dataCollectionMethod === TPGoalDataCollectionMethod.RATE) {
    total = target.sessions.reduce((sum, session) => sum + session.rate, 0);
    value = target.sessions.length ? Number((total / target.sessions.length).toFixed(2)) : 0;
  } else if (dataCollectionMethod === TPGoalDataCollectionMethod.DURATION) {
    total = target.sessions.reduce((sum, session) => sum + session.durationEventTotal, 0);
    value = target.sessions.length
      ? Number(
          (
            target.sessions.reduce((sum, session) => sum + session.durationEventAverage, 0) /
            target.sessions.length
          ).toFixed(2),
        )
      : 0;
  } else {
    total = instanceDurations.length
      ? instanceDurations.reduce((sum, duration) => sum + duration, 0)
      : target.sessions.reduce((sum, session) => sum + session.instances, 0);
    const instances = target.sessions.reduce((sum, session) => sum + session.instances, 0);
    value = instanceDurations.length
      ? instances
        ? Number((total / instances).toFixed(1))
        : 0
      : target.sessions.length
        ? Number((total / target.sessions.length).toFixed(1))
        : 0;
  }

  const summary: IDataDashboardTargetSummary = {
    targetId: target.id,
    target: target.label,
    dataCollectionMethod,
    sessionCount: target.sessions.length,
    value,
    average: value,
    total,
    hasInstanceDurations: instanceDurations.length > 0,
  };
  return summary;
}

export function buildDataDashboardReport(input: IDataDashboardReportInput): IDataDashboardReport {
  const targets = input.selectedTargets.map((target) =>
    buildTargetData(target, input.goalSummaries, input.startDate, input.endDate),
  );
  const contributingGoalIds = new Set(
    targets.flatMap((target) => target.sessions.flatMap((session) => session.source_goal_ids)),
  );

  return {
    targets,
    detailRows: buildDetailRows(targets),
    phaseRows: buildPhaseRows(input, contributingGoalIds),
    treatmentPlanRows: buildTreatmentPlanRows(input.goalSummaries, contributingGoalIds),
    baselines: buildBaselines(input.goalSummaries, contributingGoalIds),
    targetSummaries: targets.map(buildTargetSummary),
  };
}
